import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, Shield, HeadphonesIcon, CalendarDays, Users, AlertCircle, Zap, MessageCircle, ExternalLink } from "lucide-react";
import { api, fmtINR } from "@/lib/api";
import useSiteConfig, { whatsappLinkFromCfg } from "@/lib/useSiteConfig";
import LongStayNudge from "@/components/common/LongStayNudge";
import { toast } from "sonner";

const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
};
const fmtShort = (s) => new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

/**
 * Sticky booking card. Reads prefill `check_in` / `check_out` / `guests` from the URL so
 * dates selected on the search bar (or Stays grid) carry through to the property page.
 * Refetches on every change and surfaces the quote source + availability plainly.
 */
export default function StickyBookingCard({ property, onQuoteChange }) {
  const cfg = useSiteConfig();
  const [checkIn, setCheckIn] = useState(() => new URLSearchParams(window.location.search).get("check_in") || plusDays(3));
  const [checkOut, setCheckOut] = useState(() => new URLSearchParams(window.location.search).get("check_out") || plusDays(5));
  const [guests, setGuests] = useState(() => Number(new URLSearchParams(window.location.search).get("guests") || 2));
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const fetchQuote = async () => {
    if (!checkIn || !checkOut || new Date(checkOut) <= new Date(checkIn)) return;
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/quote", { slug: property.slug, start_date: checkIn, end_date: checkOut, guests });
      setQuote(data);
      // Bubble the live quote up to PropertyDetail so the mobile reserve
      // bar (and any other parent listener) shows the same price the guest
      // sees here — never a stale "from ₹X" fallback while a real quote is
      // available.
      onQuoteChange?.(data, { checkIn, checkOut, guests });
    } catch (err) {
      setQuote(null);
      onQuoteChange?.(null, { checkIn, checkOut, guests });
      toast.error(err.response?.data?.detail || "Unable to fetch quote");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn, checkOut, guests]);

  // Auto-correct the date pair so the search bar can never submit a same-day
  // or reversed range. If the new check-in lands on/after the current
  // check-out, snap check-out to check-in + 1 day. The user can still pick
  // any later check-out via the calendar; the calendar's `min` attribute
  // also blocks earlier choices at the picker level.
  const addDays = (iso, n) => {
    const d = new Date(iso);
    d.setDate(d.getDate() + n);
    return d.toISOString().split("T")[0];
  };
  const onCheckInChange = (v) => {
    setCheckIn(v);
    if (v && (!checkOut || new Date(checkOut) <= new Date(v))) {
      setCheckOut(addDays(v, 1));
    }
  };
  const checkOutMin = checkIn ? addDays(checkIn, 1) : undefined;

  const onReserve = () => {
    const params = new URLSearchParams({ slug: property.slug, check_in: checkIn, check_out: checkOut, guests: String(guests) });
    nav(`/booking?${params.toString()}`);
  };

  const isLiveRate = quote?.source === "hostify";
  const isEstimate = quote?.source === "estimated";
  const unavailable = quote && quote.available === false;

  // Total-inclusive display: roll cleaning into the displayed total line.
  const displayTotal = quote?.total || 0;

  return (
    <aside
      data-testid="sticky-booking-card"
      className="rounded-2xl border border-forest/15 bg-white p-6 shadow-card lg:sticky lg:top-24"
    >
      <div className="flex items-baseline justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-amber">From</p>
          <p className="font-display text-3xl text-forest">
            {fmtINR(quote?.nightly_price || property.nightly_price_from)}
            <span className="ml-1 text-sm font-sans text-noir/60">/ night</span>
          </p>
          {!unavailable && (() => {
            const nights = quote?.nights || 2;
            const nightly = quote?.nightly_price || property.nightly_price_from;
            const stayTotal = quote?.subtotal || nightly * nights;
            const totalSavings = Math.round(stayTotal * 0.15);
            return (
              <p className="mt-1 text-[11px] text-forest/70" data-testid="booking-airbnb-savings">
                Save ~{fmtINR(totalSavings)} vs Airbnb {quote?.nights ? `for your ${nights}-night stay` : "on a 2-night stay"}
              </p>
            );
          })()}
          <a
            href={property.airbnb_url || `https://www.airbnb.co.in/s/${encodeURIComponent(`Vyagram ${property.display_name} ${property.city || "Delhi"}`)}/homes`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="compare-airbnb-price"
            className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-forest/20 bg-white px-3 py-1 text-[11px] font-medium text-forest hover:bg-mist/40"
          >
            <ExternalLink size={11} /> Compare Airbnb price
          </a>
        </div>
        <div className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-sm" data-testid="booking-card-rating">
          <Star size={14} className="fill-gold text-gold" />
          <span className="font-medium text-forest">{property.rating?.toFixed?.(2) ?? property.rating}</span>
          <span className="text-noir/60">· {property.review_count}</span>
        </div>
      </div>

      {quote && (
        <div className="mt-3" data-testid="quote-source">
          {isLiveRate && <span className="inline-flex items-center gap-1.5 rounded-full bg-forest/10 px-2.5 py-1 text-[11px] font-medium text-forest"><Zap size={11} /> Live rate</span>}
          {isEstimate && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber/15 px-2.5 py-1 text-[11px] font-medium text-amber"><AlertCircle size={11} /> Estimate · confirm with us</span>}
          {unavailable && <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-medium text-red-700"><AlertCircle size={11} /> Not available on these dates</span>}
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-2 gap-y-6 text-sm">
        <label className="date-hint flex flex-col gap-1 rounded-xl border border-forest/15 px-3 py-2.5 hover:bg-mist/50" data-hint="dd/mm/yyyy">
          <span className="text-[10px] uppercase tracking-wider text-amber">Check in</span>
          <input type="date" min={plusDays(0)} value={checkIn} onChange={(e) => setCheckIn(e.target.value)} className="bg-transparent focus:outline-none" data-testid="booking-checkin" />
        </label>
        <label className="date-hint flex flex-col gap-1 rounded-xl border border-forest/15 px-3 py-2.5 hover:bg-mist/50" data-hint="dd/mm/yyyy">
          <span className="text-[10px] uppercase tracking-wider text-amber">Check out</span>
          <input type="date" min={checkOutMin} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} className="bg-transparent focus:outline-none" data-testid="booking-checkout" />
        </label>
        <label className="col-span-2 flex items-center gap-2 rounded-xl border border-forest/15 px-3 py-2.5 hover:bg-mist/50">
          <Users size={16} className="text-forest" />
          <div className="flex-1">
            <span className="block text-[10px] uppercase tracking-wider text-amber">Guests</span>
            <select value={guests} onChange={(e) => setGuests(Number(e.target.value))} className="w-full bg-transparent focus:outline-none" data-testid="booking-guests">
              {Array.from({ length: property.guests }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{n} {n === 1 ? "guest" : "guests"}</option>
              ))}
            </select>
          </div>
        </label>
      </div>

      {unavailable ? (
        <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4" data-testid="unavailable-panel">
          <p className="text-sm font-medium text-red-800">These dates aren't open.</p>
          {quote.unavailable_dates?.length > 0 && (
            <p className="mt-1 text-xs text-red-700">Blocked: {quote.unavailable_dates.slice(0, 6).map(fmtShort).join(", ")}{quote.unavailable_dates.length > 6 ? "…" : ""}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <a href={whatsappLinkFromCfg(cfg, `Hi Vyagram, I'm looking at ${property.display_name} for ${fmtShort(checkIn)} — ${fmtShort(checkOut)} but the site shows it's blocked. Do you have alternate dates?`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-medium text-white"><MessageCircle size={14} /> Talk to us</a>
            <Link
              to={`/stays?check_in=${checkIn}&check_out=${checkOut}&guests=${guests}`}
              className="inline-flex items-center gap-2 rounded-full border border-forest/30 px-4 py-2 text-sm text-forest hover:bg-mist/60"
              data-testid="see-other-stays-cta"
            >
              See other stays for {fmtShort(checkIn)} — {fmtShort(checkOut)}
            </Link>
          </div>
        </div>
      ) : quote ? (
        <div data-testid="booking-quote" className="mt-5 space-y-1.5 rounded-xl bg-mist/60 p-4 text-sm">
          <div className="flex justify-between"><span className="text-noir/70">{fmtINR(quote.nightly_price)} × {quote.nights} nights</span><span>{fmtINR(quote.subtotal)}</span></div>
          <div className="mt-2 flex justify-between border-t border-forest/10 pt-2 font-medium text-forest">
            <span>Total (incl. cleaning &amp; taxes)</span><span className="font-display text-lg">{fmtINR(displayTotal)}</span>
          </div>
          {quote.security_deposit > 0 && (
            <p className="pt-1 text-[11px] text-noir/60">+ {fmtINR(quote.security_deposit)} refundable security deposit (collected separately)</p>
          )}
          {quote.notice && <p className="pt-1 text-[11px] text-amber">{quote.notice}</p>}
        </div>
      ) : null}

      <button onClick={onReserve} disabled={loading || !quote || unavailable} className="vyg-cta mt-5 w-full disabled:opacity-40 disabled:cursor-not-allowed" data-testid="reserve-cta">
        {loading ? "Checking…" : unavailable ? "Dates unavailable" : "Reserve your stay"}
      </button>

      {quote?.nights >= 30 && (
        <div className="mt-4">
          <LongStayNudge nights={quote.nights} compact />
        </div>
      )}

      <div className="mt-5 grid gap-2 text-xs text-noir/70">
        <div className="flex items-start gap-2"><Shield size={14} className="mt-0.5 text-forest" /> Secure direct booking. No OTA fees.</div>
        <div className="flex items-start gap-2"><HeadphonesIcon size={14} className="mt-0.5 text-forest" /> Vyagram on-ground team available across your stay.</div>
        <div className="flex items-start gap-2"><CalendarDays size={14} className="mt-0.5 text-forest" /> Flexible dates — modify up to 72 hrs before check-in.</div>
      </div>
    </aside>
  );
}
